import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import {
   bulkDeleteVouchersApi,
   getVoucherBatchApi,
   getVoucherStatsApi,
   listDeviceVouchersApi,
   listDevices,
   type ApiDeviceVoucher,
   type VoucherPrintLayout,
} from '@/api/vendo';
import { relativeTime, exactTime } from '@/utils/relativeTime';
import Pagination from '@/components/Admin/UI/Pagination';
import { SkeletonRows } from '@/components/Admin/UI/Skeleton';
import { GenerateVouchersWizard, STORE_NAME_KEY, vendoReach } from '@/components/Admin/Prompt/GenerateVouchersWizard';
import {
   VoucherPrintHost,
   VoucherCard,
   CardProbe,
   CARDS_PER_A4_OPTIONS,
   a4Grid,
   sheetsFor,
   useMeasuredCardHeights,
   displayCode,
   durationWords,
   type VoucherCardData,
} from '@/components/Admin/Prompt/VoucherPrintSheet';
import type { VoucherCardStyle } from '@/api/vendo';

/**
 * /wifi-vouchers?tab=counter — "Over the counter" (Ray's approved redesign,
 * 2026-10-14). ONE table of every printed voucher across ALL vendos, with
 * search, a vendo filter, status chips, "Reprint selected" and the stat
 * tiles; ONE "+ Generate vouchers" button that opens the 4-step wizard.
 * Replaces the "Choose a vendo + side drawer" page Ray called "very bad UI".
 */

type Chip = '' | 'live' | 'redeemed' | 'expired';
const CHIPS: { value: Chip; label: string }[] = [
   { value: '', label: 'All' },
   { value: 'live', label: 'Unredeemed' },
   { value: 'redeemed', label: 'Redeemed' },
   { value: 'expired', label: 'Expired' },
];
/** Rows per page (Ray 2026-10-18: a reprint was stuck at 20 = one page).
 *  100 is the server's cap for a list page. Remembered per browser. */
const PAGE_SIZES = [20, 50, 100] as const;
const PAGE_SIZE_KEY = 'smartfi-otc-page-size';
function savedPageSize(): number {
   try {
      const n = Number(localStorage.getItem(PAGE_SIZE_KEY));
      return (PAGE_SIZES as readonly number[]).includes(n) ? n : 20;
   } catch {
      return 20;
   }
}

export type RowStatus = 'unredeemed' | 'redeemed' | 'expired';
export function rowStatus(v: Pick<ApiDeviceVoucher, 'sold' | 'validUntil'>, now = Date.now()): RowStatus {
   if (v.sold) return 'redeemed';
   if (v.validUntil && new Date(v.validUntil).getTime() < now) return 'expired';
   return 'unredeemed';
}

/** "All 4 vendos" / "3 vendos" / the one vendo's name — and the small line under it. */
export function vendoCell(
   v: Pick<ApiDeviceVoucher, 'vendoDevice' | 'vendoDevices'>,
   totalVendos: number
): { main: string; sub: string } {
   const many = v.vendoDevices ?? [];
   if (many.length > 1) {
      if (totalVendos && many.length >= totalVendos) return { main: `All ${many.length} vendos`, sub: 'works on any of them' };
      return { main: `${many.length} vendos`, sub: many.map((d) => d.name ?? '').filter(Boolean).join(', ') };
   }
   const one = many[0] ?? (typeof v.vendoDevice === 'object' && v.vendoDevice ? v.vendoDevice : null);
   if (one) return { main: one.name ?? 'Vendo', sub: (one as { deviceId?: string }).deviceId ?? '' };
   return { main: 'Any of your vendos', sub: 'older code' };
}

function shortDate(iso: string | null | undefined): string {
   if (!iso) return '';
   const d = new Date(iso);
   if (Number.isNaN(d.getTime())) return '';
   const sameYear = d.getFullYear() === new Date().getFullYear();
   return d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** `fixedDeviceId`: the buyer's sub-login page (Ray 2026-10-18) — one vendo,
 *  no vendo filter, the wizard pre-answers its Vendos step. */
const WifiVouchersCounter: React.FC<{ fixedDeviceId?: string }> = ({ fixedDeviceId }) => {
   const queryClient = useQueryClient();
   const [q, setQ] = useState('');
   const [qDebounced, setQDebounced] = useState('');
   const [vendo, setVendo] = useState(fixedDeviceId ?? '');
   const [onlineOnly, setOnlineOnly] = useState(false);
   const [chip, setChip] = useState<Chip>('');
   const [page, setPage] = useState(1);
   const [pageSize, setPageSizeState] = useState<number>(savedPageSize);
   const setPageSize = (n: number) => {
      setPageSizeState(n);
      setPage(1);
      try { localStorage.setItem(PAGE_SIZE_KEY, String(n)); } catch { /* private mode */ }
   };
   const [selected, setSelected] = useState<Set<string>>(new Set());
   const [wizardOpen, setWizardOpen] = useState(false);
   const [reprint, setReprint] = useState<{ groups: { cards: VoucherCardData[]; layout: VoucherPrintLayout }[] } | null>(null);
   const [reprinting, setReprinting] = useState(false);
   // "Reprint selected" first asks for ONE design (Ray 2026-10-18: codes from
   // three batches printed as three half-empty runs, one per old design).
   const [reprintSetup, setReprintSetup] = useState<{ cards: VoucherCardData[]; layout: VoucherPrintLayout } | null>(null);
   const [cardMm, setCardMm] = useMeasuredCardHeights();
   const [confirmDelete, setConfirmDelete] = useState(false);
   const [deleting, setDeleting] = useState(false);

   useEffect(() => {
      const t = setTimeout(() => setQDebounced(q.trim()), 250);
      return () => clearTimeout(t);
   }, [q]);
   useEffect(() => { setPage(1); }, [qDebounced, vendo, chip]);

   const { data: devicesData } = useQuery({ queryKey: ['otc-wizard-vendos'], queryFn: () => listDevices() });
   const devices = devicesData?.devices ?? [];
   // The vendo filter's options: every vendo, or only the ones the Devices
   // page would show as reachable (Online or Degraded — same rule).
   const filterDevices = useMemo(
      () => (onlineOnly ? devices.filter((d) => vendoReach(d) !== 'offline') : devices),
      [devices, onlineOnly]
   );
   // The chosen vendo dropped out of the list (went offline, filter on):
   // fall back to "All vendos" rather than a blank dropdown.
   useEffect(() => {
      if (vendo && devices.length && !filterDevices.some((d) => d._id === vendo)) setVendo('');
   }, [vendo, devices.length, filterDevices]);

   const { data: stats } = useQuery({
      queryKey: ['otc-voucher-stats', vendo],
      queryFn: () => getVoucherStatsApi(vendo || undefined),
   });

   const listParams = (p: number) => ({
      ...(chip ? { status: chip } : {}),
      ...(vendo ? { vendo } : {}),
      ...(qDebounced ? { q: qDebounced } : {}),
      page: p,
      limit: pageSize,
   });
   const { data, isLoading } = useQuery({
      queryKey: ['otc-vouchers', chip, vendo, qDebounced, page, pageSize],
      queryFn: () => listDeviceVouchersApi(listParams(page)),
      // Paging / filtering keeps the rows on screen until the next set lands
      // (no blank table); the grey rows show only on the very first load.
      placeholderData: keepPreviousData,
   });
   const rows = data?.vouchers ?? [];
   const total = data?.total ?? 0;
   const pageCount = Math.max(1, Math.ceil(total / pageSize));
   // Fetch the next page ahead, so "Next" is instant (Ray 2026-10-18: slow).
   const qcPrefetch = useQueryClient();
   useEffect(() => {
      if (page < pageCount) {
         qcPrefetch.prefetchQuery({
            queryKey: ['otc-vouchers', chip, vendo, qDebounced, page + 1, pageSize],
            queryFn: () => listDeviceVouchersApi(listParams(page + 1)),
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [page, pageCount, chip, vendo, qDebounced, pageSize]);

   // Rows seen across pages, so a reprint can mix page 1 and page 3.
   const seenRef = useRef<Map<string, ApiDeviceVoucher>>(new Map());
   rows.forEach((v) => seenRef.current.set(v._id, v));

   const toggle = (id: string) =>
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   const allOnPage = rows.length > 0 && rows.every((v) => selected.has(v._id));
   const toggleAll = () =>
      setSelected((prev) => {
         const next = new Set(prev);
         if (allOnPage) rows.forEach((v) => next.delete(v._id));
         else rows.forEach((v) => next.add(v._id));
         return next;
      });

   /** Reprint: every ticked code in ONE design the operator picks in a popup,
    *  pre-filled from the first ticked code's print run (store name, logo,
    *  style, size). Codes with no run (older) use the last-used store name. */
   const handleReprint = async () => {
      if (reprinting || !selected.size) return;
      const picked = Array.from(selected).map((id) => seenRef.current.get(id)).filter((v): v is ApiDeviceVoucher => !!v);
      if (!picked.length) return;
      setReprinting(true);
      try {
         let fallbackStore = '';
         try { fallbackStore = localStorage.getItem(STORE_NAME_KEY) || ''; } catch { /* private mode */ }
         const byBatch = new Map<string, ApiDeviceVoucher[]>();
         for (const v of picked) {
            const id = typeof v.batch === 'object' && v.batch ? v.batch._id : typeof v.batch === 'string' ? v.batch : '';
            const list = byBatch.get(id) ?? [];
            list.push(v);
            byBatch.set(id, list);
         }
         let layout: VoucherPrintLayout = { storeName: fallbackStore, logoDataUrl: '', cardsPerA4: 20, cardStyle: 'classic' };
         const firstBatch = Array.from(byBatch.keys()).find(Boolean);
         if (firstBatch) {
            try {
               const r = await getVoucherBatchApi(firstBatch);
               layout = { ...layout, ...r.batch.print };
            } catch {
               // The run's layout is gone — start from the default one.
            }
         }
         const cards = Array.from(byBatch.values()).flat().map((v) => ({
            code: displayCode(v.code, v.prefix),
            price: v.price,
            duration: v.duration,
            free: !!v.free,
            validityText: v.validUntil ? `valid until ${shortDate(v.validUntil)}` : '',
         }));
         setReprintSetup({ cards, layout });
      } catch (err: any) {
         toast.error(err?.response?.data?.error ?? 'Could not prepare the reprint');
      } finally {
         setReprinting(false);
      }
   };

   /** "Show N per page" — at the top AND under the table (Ray 2026-10-18). */
   const perPage = (where: 'top' | 'bottom') => (
      <PerPage>
         Show
         <Select
            value={String(pageSize)}
            onChange={(e) => setPageSize(Number(e.target.value))}
            aria-label={where === 'top' ? 'Codes per page' : 'Codes per page (bottom)'}
         >
            {PAGE_SIZES.map((n) => (
               <option key={n} value={n}>{n}</option>
            ))}
         </Select>
         per page
      </PerPage>
   );

   // What the ticked rows are: only UNREDEEMED codes can be deleted; redeemed
   // ones are skipped (the server refuses them too — includeRedeemed stays off).
   const pickedRows = Array.from(selected).map((id) => seenRef.current.get(id)).filter((v): v is ApiDeviceVoucher => !!v);
   const deletableIds = pickedRows.filter((v) => !v.sold).map((v) => v._id);
   const redeemedPicked = pickedRows.length - deletableIds.length;
   useEffect(() => { if (!selected.size) setConfirmDelete(false); }, [selected.size]);

   const handleDelete = async () => {
      if (deleting) return;
      if (!deletableIds.length) {
         toast.info(`${redeemedPicked} redeemed code${redeemedPicked === 1 ? ' was' : 's were'} skipped — redeemed codes are kept`);
         setConfirmDelete(false);
         return;
      }
      setDeleting(true);
      try {
         const r = await bulkDeleteVouchersApi(deletableIds, false);
         const skipped = redeemedPicked + (r.skipped ?? 0);
         toast.success(`Deleted ${r.deleted} code${r.deleted === 1 ? '' : 's'}`);
         if (skipped) toast.info(`${skipped} redeemed code${skipped === 1 ? ' was' : 's were'} skipped — redeemed codes are kept`);
         setSelected((prev) => {
            const next = new Set(prev);
            deletableIds.forEach((id) => { next.delete(id); seenRef.current.delete(id); });
            return next;
         });
         setConfirmDelete(false);
         queryClient.invalidateQueries({ queryKey: ['otc-vouchers'] });
         queryClient.invalidateQueries({ queryKey: ['otc-voucher-stats'] });
         queryClient.invalidateQueries({ queryKey: ['device-vouchers'] });
      } catch (err: any) {
         toast.error(err?.response?.data?.error ?? 'Could not delete those codes');
      } finally {
         setDeleting(false);
      }
   };

   const fmtPeso = (n: number) => `₱${Math.round(n).toLocaleString()}`;

   return (
      <Wrap>
         <Top>
            <div>
               <H1>{fixedDeviceId ? 'Vouchers' : 'Over the counter'}</H1>
               <Lead>{fixedDeviceId ? 'Every printed voucher for this vendo. Customers type the code on the WiFi page.' : 'Every printed voucher across all your vendos. Customers type the code on the WiFi page.'}</Lead>
            </div>
            <Primary type="button" onClick={() => setWizardOpen(true)} data-testid="otc-generate">
               + Generate vouchers
            </Primary>
         </Top>

         <Stats>
            <Stat><b>{stats ? stats.unredeemed.toLocaleString() : '—'}</b><span>Unredeemed</span></Stat>
            <Stat><b>{stats ? stats.soldThisMonth.toLocaleString() : '—'}</b><span>Sold this month</span></Stat>
            <Stat><b>{stats ? stats.expired.toLocaleString() : '—'}</b><span>Expired</span></Stat>
            <Stat><b>{stats ? fmtPeso(stats.unredeemedValue) : '—'}</b><span>Printed value unredeemed</span></Stat>
         </Stats>

         <Card>
            <Bar>
               <Search
                  placeholder="Search code or batch label…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label="Search code or batch label"
               />
               {!fixedDeviceId && (
               <VendoFilter>
                  <Select value={vendo} onChange={(e) => setVendo(e.target.value)} aria-label="Vendo">
                     <option value="">All vendos</option>
                     {filterDevices.map((d) => (
                        <option key={d._id} value={d._id}>{d.parent ? '↳ ' : ''}{d.name}</option>
                     ))}
                  </Select>
                  <SmallToggle>
                     <input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} />
                     Online vendos only
                  </SmallToggle>
               </VendoFilter>
               )}
               <Chips>
                  {CHIPS.map((c) => (
                     <ChipBtn key={c.value} type="button" $on={chip === c.value} onClick={() => setChip(c.value)}>
                        {c.label}
                     </ChipBtn>
                  ))}
               </Chips>
            </Bar>
            {/* Row 2 (Ray 2026-10-18): actions on the left, "Show N per page" on the right. */}
            <ActionBar data-testid="otc-action-bar">
               <Actions>
                  <ActionBtn type="button" disabled={!selected.size || reprinting} onClick={handleReprint} data-testid="otc-reprint">
                     <PrinterIcon />
                     {reprinting ? 'Preparing…' : selected.size ? `Reprint selected (${selected.size})` : 'Reprint selected'}
                  </ActionBtn>
                  {confirmDelete && selected.size ? (
                     <Confirm role="group" aria-label="Confirm delete" data-testid="otc-delete-confirm">
                        <span>
                           Delete {deletableIds.length} code{deletableIds.length === 1 ? '' : 's'}?
                           {redeemedPicked ? ` (${redeemedPicked} redeemed will be kept)` : ''}
                        </span>
                        <Danger type="button" disabled={deleting} onClick={handleDelete} data-testid="otc-delete-yes">
                           {deleting ? 'Deleting…' : 'Yes, delete'}
                        </Danger>
                        <Ghost type="button" disabled={deleting} onClick={() => setConfirmDelete(false)}>Keep</Ghost>
                     </Confirm>
                  ) : (
                     <ActionBtn type="button" $danger disabled={!selected.size || deleting} onClick={() => setConfirmDelete(true)} data-testid="otc-delete">
                        <TrashIcon />
                        {selected.size ? `Delete selected (${selected.size})` : 'Delete selected'}
                     </ActionBtn>
                  )}
               </Actions>
               {perPage('top')}
            </ActionBar>

            <TableWrap>
               <Table>
                  <thead>
                     <tr>
                        <th><input type="checkbox" checked={allOnPage} onChange={toggleAll} aria-label="Select all on this page" /></th>
                        <th>Code</th>
                        <th>Vendo</th>
                        <th>Rate</th>
                        <th>Batch</th>
                        <th>Status</th>
                        <th>Printed</th>
                        <th>Expires</th>
                     </tr>
                  </thead>
                  <tbody>
                     {/* Grey rows while loading, like every other list (Ray 2026-10-18). */}
                     {isLoading && <SkeletonRows rows={8} cols={8} />}
                     {!isLoading && rows.length === 0 && (
                        <tr>
                           <td colSpan={8}>
                              <Muted>
                                 {qDebounced || vendo || chip
                                    ? 'No vouchers match these filters.'
                                    : 'No printed vouchers yet. Press "+ Generate vouchers" to print your first batch.'}
                              </Muted>
                           </td>
                        </tr>
                     )}
                     {rows.map((v) => {
                        const st = rowStatus(v);
                        const cell = vendoCell(v, devices.length);
                        const speed = v.downloadMbps ? ` · ${v.downloadMbps} Mbps` : '';
                        const batchLabel = v.batchLabel || (typeof v.batch === 'object' && v.batch ? v.batch.label : '') || '';
                        return (
                           <tr key={v._id} data-status={st}>
                              <td><input type="checkbox" checked={selected.has(v._id)} onChange={() => toggle(v._id)} aria-label={`Select ${v.code}`} /></td>
                              <td><CodeText>{displayCode(v.code, v.prefix)}</CodeText></td>
                              <td><Vendo>{cell.main}{cell.sub ? <small>{cell.sub}</small> : null}</Vendo></td>
                              <td>
                                 <Rate>
                                    {v.free || v.price === 0 ? <Pill $kind="new">FREE</Pill> : <b>₱{v.price}</b>}
                                    <small>{v.capMb ? `${v.capMb} MB` : durationWords(v.duration)}{speed}</small>
                                 </Rate>
                              </td>
                              <td>{batchLabel || <Muted>—</Muted>}</td>
                              <td>
                                 <Pill $kind={st === 'redeemed' ? 'used' : st === 'expired' ? 'exp' : 'new'}>
                                    {st === 'redeemed' ? 'Redeemed' : st === 'expired' ? 'Expired' : 'Unredeemed'}
                                 </Pill>
                              </td>
                              <td title={exactTime(v.createdAt)}>{relativeTime(v.createdAt)}</td>
                              <td>
                                 {st === 'redeemed'
                                    ? <Muted title={exactTime(v.redeemedAt)}>used {shortDate(v.redeemedAt) || relativeTime(v.redeemedAt)}</Muted>
                                    : v.validUntil
                                    ? st === 'expired' ? <Muted>{shortDate(v.validUntil)}</Muted> : shortDate(v.validUntil)
                                    : <Muted>Never</Muted>}
                              </td>
                           </tr>
                        );
                     })}
                  </tbody>
               </Table>
            </TableWrap>
            {total > 0 && (
               <Pages>
                  {perPage('bottom')}
                  <Pagination page={page} pageCount={pageCount} onChange={setPage} totalCount={total} pageSize={pageSize} />
               </Pages>
            )}
         </Card>

         {wizardOpen && (
            <GenerateVouchersWizard onClose={() => setWizardOpen(false)} fixedVendoIds={fixedDeviceId ? [fixedDeviceId] : undefined} />
         )}
         {reprintSetup && (
            <SetupOverlay>
               <SetupDialog role="dialog" aria-label="Reprint vouchers">
                  <h3>Reprint {reprintSetup.cards.length} code{reprintSetup.cards.length === 1 ? '' : 's'}</h3>
                  <SetupField>
                     <span>Card style</span>
                     <StyleChoices role="radiogroup" aria-label="Card style">
                        {([['classic', 'Classic ticket'], ['strip', 'Compact strip'], ['bold', 'Bold']] as [VoucherCardStyle, string][]).map(([v, label]) => (
                           <StyleChoice
                              key={v}
                              type="button"
                              role="radio"
                              aria-checked={reprintSetup.layout.cardStyle === v}
                              $on={reprintSetup.layout.cardStyle === v}
                              onClick={() => setReprintSetup({ ...reprintSetup, layout: { ...reprintSetup.layout, cardStyle: v } })}
                           >
                              {label}
                           </StyleChoice>
                        ))}
                     </StyleChoices>
                  </SetupField>
                  <SetupField>
                     <label htmlFor="otc-reprint-size">Card size</label>
                     <Select
                        id="otc-reprint-size"
                        value={String(reprintSetup.layout.cardsPerA4)}
                        onChange={(e) =>
                           setReprintSetup({
                              ...reprintSetup,
                              layout: { ...reprintSetup.layout, cardsPerA4: Number(e.target.value) as VoucherPrintLayout['cardsPerA4'] },
                           })
                        }
                     >
                        {CARDS_PER_A4_OPTIONS.map((o) => (
                           <option key={o.value} value={o.value}>
                              {o.label} · {a4Grid(o.value, reprintSetup.layout.cardStyle, cardMm[o.value]).perSheet} per A4
                           </option>
                        ))}
                     </Select>
                  </SetupField>
                  <SetupPreview>
                     <VoucherCard card={reprintSetup.cards[0]} layout={reprintSetup.layout} logoDataUrl={reprintSetup.layout.logoDataUrl} />
                  </SetupPreview>
                  <CardProbe card={reprintSetup.cards[0]} layout={reprintSetup.layout} onMeasure={setCardMm} />
                  <SetupNote data-testid="otc-reprint-sheets">
                     {reprintSetup.cards.length} code{reprintSetup.cards.length === 1 ? '' : 's'} ={' '}
                     {sheetsFor(reprintSetup.cards.length, reprintSetup.layout.cardsPerA4, reprintSetup.layout.cardStyle, cardMm[reprintSetup.layout.cardsPerA4])} A4 sheet
                     {sheetsFor(reprintSetup.cards.length, reprintSetup.layout.cardsPerA4, reprintSetup.layout.cardStyle, cardMm[reprintSetup.layout.cardsPerA4]) === 1 ? '' : 's'}. Each card keeps its own price and time.
                  </SetupNote>
                  <SetupButtons>
                     <Ghost type="button" onClick={() => setReprintSetup(null)}>Cancel</Ghost>
                     <Primary
                        type="button"
                        data-testid="otc-reprint-print"
                        onClick={() => {
                           setReprint({ groups: [{ cards: reprintSetup.cards, layout: reprintSetup.layout }] });
                           setReprintSetup(null);
                           setTimeout(() => { try { window.print(); } catch { /* jsdom */ } }, 300);
                        }}
                     >
                        Print
                     </Primary>
                  </SetupButtons>
               </SetupDialog>
            </SetupOverlay>
         )}
         {reprint && reprint.groups.map((g, i) => (
            <VoucherPrintHost key={i} cards={g.cards} layout={g.layout} />
         ))}
      </Wrap>
   );
};

export default WifiVouchersCounter;

/* ── styles ──────────────────────────────────────────────────────────── */

const Wrap = styled.div`
   margin: 1rem 1rem 0;
   @media (max-width: 768px) {
      margin: 12px 0 0;
   }
   @media print {
      display: none;
   }
`;

const Top = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
   margin-bottom: 16px;
`;

const H1 = styled.h2`
   font-size: 20px;
   margin: 0;
   color: ${colors.GRAY1};
`;

const Lead = styled.p`
   color: ${colors.GRAY3};
   margin: 2px 0 0;
   font-size: 13px;
`;

const Stats = styled.div`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   gap: 12px;
   margin-bottom: 16px;
   @media (max-width: 768px) {
      grid-template-columns: 1fr 1fr;
   }
`;

const Stat = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   border-radius: 12px;
   padding: 12px 14px;
   b {
      display: block;
      font-size: 22px;
      font-variant-numeric: tabular-nums;
      color: ${colors.GRAY1};
   }
   span {
      color: ${colors.GRAY3};
      font-size: 12px;
   }
`;

const Card = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   border-radius: 12px;
   overflow: hidden;
`;

const Bar = styled.div`
   display: flex;
   gap: 10px;
   flex-wrap: wrap;
   align-items: center;
   padding: 12px 14px;
   border-bottom: 1px solid ${colors.GRAY13};
`;

const ActionBar = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   flex-wrap: wrap;
   padding: 10px 14px;
   border-bottom: 1px solid ${colors.GRAY13};
`;
const Actions = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

const Search = styled.input`
   flex: 1;
   min-width: 160px;
   border: 1px solid ${colors.GRAY13};
   background: ${colors.WHITE};
   color: ${colors.GRAY1};
   border-radius: 9px;
   padding: 8px 10px;
   font: inherit;
   font-size: 13.5px;
`;

const Select = styled.select`
   border: 1px solid ${colors.GRAY13};
   background: ${colors.WHITE};
   color: ${colors.GRAY1};
   border-radius: 9px;
   padding: 8px 10px;
   font: inherit;
   font-size: 13.5px;
   max-width: 240px;
`;

const VendoFilter = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const SmallToggle = styled.label`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 12.5px;
   color: ${colors.GRAY3};
   cursor: pointer;
   white-space: nowrap;
   input {
      width: 15px;
      height: 15px;
      margin: 0;
   }
`;

const Chips = styled.div`
   display: flex;
   gap: 6px;
   margin-left: auto;
   flex-wrap: wrap;
`;

/* Inline "Delete N codes? [Yes, delete] [Keep]" — lives in the toolbar,
 * never a second modal. */
const Confirm = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
   padding: 4px 6px 4px 10px;
   border: 1px solid #f5c2c7;
   background: #fff5f5;
   border-radius: 9px;
   font-size: 13px;
   color: ${colors.GRAY1};
   span {
      font-weight: 600;
   }
`;

const ChipBtn = styled.button<{ $on: boolean }>`
   border: 1px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY13)};
   border-radius: 999px;
   padding: 5px 11px;
   font-size: 12.5px;
   font-weight: 600;
   cursor: pointer;
   background: ${({ $on }) => ($on ? colors.PRIMARY : colors.WHITE)};
   color: ${({ $on }) => ($on ? '#fff' : colors.GRAY1)};
`;

const TableWrap = styled.div`
   overflow-x: auto;
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   min-width: 820px;
   th {
      font-size: 11.5px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${colors.GRAY3};
      text-align: left;
      padding: 10px 14px;
      border-bottom: 1px solid ${colors.GRAY13};
      font-weight: 600;
   }
   td {
      padding: 11px 14px;
      border-bottom: 1px solid ${colors.GRAY13};
      vertical-align: middle;
      font-variant-numeric: tabular-nums;
      font-size: 13.5px;
      color: ${colors.GRAY1};
   }
   tr:last-child td {
      border-bottom: 0;
   }
   input[type='checkbox'] {
      width: 16px;
      height: 16px;
   }
`;

const CodeText = styled.code`
   font: 600 13px ui-monospace, SFMono-Regular, Menlo, monospace;
   letter-spacing: 0.04em;
`;

const Vendo = styled.div`
   font-weight: 600;
   small {
      display: block;
      color: ${colors.GRAY3};
      font-weight: 400;
      font-size: 11.5px;
   }
`;

const Rate = styled.div`
   b {
      display: block;
   }
   small {
      display: block;
      color: ${colors.GRAY3};
      font-size: 11.5px;
   }
`;

const Pill = styled.span<{ $kind: 'new' | 'used' | 'exp' }>`
   display: inline-block;
   border-radius: 999px;
   padding: 3px 9px;
   font-size: 11.5px;
   font-weight: 700;
   background: ${({ $kind }) => ($kind === 'new' ? '#e3f5ea' : $kind === 'used' ? '#eef0f4' : '#fdf1dc')};
   color: ${({ $kind }) => ($kind === 'new' ? '#1f9d55' : $kind === 'used' ? '#8a90a3' : '#b7791f')};
`;

const Muted = styled.span`
   color: ${colors.GRAY3};
`;

const Pages = styled.div`
   padding: 6px 14px;
   border-top: 1px solid ${colors.GRAY13};
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;
const PerPage = styled.label`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   font-size: 13px;
   color: ${colors.GRAY5};
   select { width: auto; min-width: 72px; }
`;

/* Reprint design popup. A form: closes only by Cancel or Print. */
const sheetUp = keyframes`
   from { transform: translateY(100%); }
   to { transform: none; }
`;
const SetupOverlay = styled.div`
   position: fixed; inset: 0; z-index: 1000;
   background: rgba(0, 0, 0, 0.45);
   display: flex; align-items: center; justify-content: center; padding: 16px;
   /* Phones: a sheet that slides up from the bottom, like every window (Ray 2026-10-18). */
   @media (max-width: 768px) { align-items: flex-end; padding: 0; }
`;
const SetupDialog = styled.div`
   background: ${colors.WHITE}; color: ${colors.MAIN_DARK}; border-radius: 8px; padding: 20px; /* standard window corners; theme colours for dark mode */
   width: 100%; max-width: 480px; max-height: 90vh; overflow: auto;
   display: flex; flex-direction: column; gap: 14px;
   h3 { margin: 0; font-size: 18px; color: ${colors.MAIN_DARK}; }
   @media (max-width: 768px) {
      max-width: none; max-height: 88vh;
      border-radius: 18px 18px 0 0;
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
      animation: ${sheetUp} 0.22s ease-out;
      @media (prefers-reduced-motion: reduce) { animation: none; }
   }
`;
const SetupField = styled.div`
   display: flex; flex-direction: column; gap: 6px;
   > span, > label { font-size: 13px; font-weight: 600; color: ${colors.MAIN_DARK}; }
   > select { width: 100%; max-width: none; }
`;
/* Full width, equal thirds — no empty space at the right (Ray 2026-10-18). */
const StyleChoices = styled.div`display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px;`;
const StyleChoice = styled.button<{ $on: boolean }>`
   border: 1px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on }) => ($on ? colors.PRIMARY : colors.WHITE)};
   color: ${({ $on }) => ($on ? '#fff' : colors.MAIN_DARK)};
   border-radius: 8px; padding: 7px 12px; font-size: 13px; font-weight: 600; cursor: pointer;
`;
const SetupPreview = styled.div`
   display: flex; justify-content: center; padding: 12px;
   background: ${colors.GRAY14}; border-radius: 10px;
`;
const SetupNote = styled.div`font-size: 13px; color: ${colors.GRAY5};`;
const SetupButtons = styled.div`display: flex; justify-content: flex-end; gap: 8px;`;

const Primary = styled.button`
   background: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY};
   color: #fff;
   padding: 9px 14px;
   border-radius: 9px;
   font: inherit;
   font-weight: 600;
   font-size: 13.5px;
   cursor: pointer;
`;

/* Toolbar actions read as actions: an icon + the brand / danger colour once
   something is ticked (Ray 2026-10-18: "looks like a normal button"). */
const ActionBtn = styled.button<{ $danger?: boolean }>`
   display: inline-flex; align-items: center; gap: 7px;
   padding: 8px 14px; border-radius: 8px;
   font: inherit; font-weight: 600; font-size: 13px; cursor: pointer;
   border: 1px solid ${({ $danger }) => ($danger ? colors.RED : colors.PRIMARY)};
   background: ${({ $danger }) => ($danger ? 'rgba(220, 53, 69, 0.08)' : `${colors.PRIMARY}14`)};
   color: ${({ $danger }) => ($danger ? colors.RED : colors.PRIMARY)};
   svg { flex: none; }
   &:disabled {
      border-color: ${colors.GRAY13}; background: ${colors.WHITE}; color: ${colors.GRAY5};
      cursor: not-allowed;
   }
`;
const iconProps = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
const PrinterIcon = () => (
   <svg {...iconProps}>
      <path d="M6 9V2h12v7" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <path d="M6 14h12v8H6z" />
   </svg>
);
const TrashIcon = () => (
   <svg {...iconProps}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
   </svg>
);

const Ghost = styled.button`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   color: ${colors.GRAY1};
   padding: 8px 14px;
   border-radius: 9px;
   font: inherit;
   font-weight: 600;
   font-size: 13px;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const Danger = styled(Ghost)`
   background: #c53030;
   border-color: #c53030;
   color: #fff;
`;
